
import React from "react";
import ReactMarkdown from "react-markdown";
import { DiagnosticUtils } from "../../utils/diagnosticUtils";
import { preprocessFeedbackForMarkdown, validateMarkdownContent, parseJSONFeedback, isJSONFeedbackFormat, convertJSONFeedbackToMarkdown } from "../../utils/enhancedFeedbackDetection";
import FeedbackActionButtons from "./FeedbackActionButtons";

interface FeedbackRendererProps {
  content: string;
}

const FeedbackRenderer: React.FC<FeedbackRendererProps> = ({ content }) => {
  
  console.log('🎯 FEEDBACK-RENDERER: Starting processing');
  console.log('🎯 FEEDBACK-RENDERER: Raw content length:', content.length);
  console.log('🎯 FEEDBACK-RENDERER: Raw content preview:', content.substring(0, 300));
  
  DiagnosticUtils.testFeedbackDetection(content);
  
  // Check if this is JSON feedback format
  if (isJSONFeedbackFormat(content)) {
    const jsonFeedback = parseJSONFeedback(content);
    if (jsonFeedback) {
      console.log('🎯 FEEDBACK-RENDERER: Rendering JSON feedback format');
      const markdownContent = convertJSONFeedbackToMarkdown(jsonFeedback);
      return (
        <div className="relative">
          <JSONFeedbackRenderer feedback={jsonFeedback} />
          <FeedbackActionButtons content={markdownContent} />
        </div>
      );
    }
  }
  
  // Legacy plain text processing (backwards compatibility)
  console.log('🎯 FEEDBACK-RENDERER: Processing legacy plain text format');
  
  let processedContent = content;
  processedContent = processedContent.replace(/\r\n/g, '\n');
  processedContent = processedContent.replace(/\r/g, '\n');
  processedContent = processedContent.replace(/\\n/g, '\n');
  processedContent = processedContent.replace(/\\r\\n/g, '\n');
  
  const validation = validateMarkdownContent(processedContent);
  console.log('🎯 FEEDBACK-RENDERER: Content validation:', validation);
  
  processedContent = preprocessFeedbackForMarkdown(processedContent);
  
  if (!validation.isValid) {
    console.warn('⚠️ FEEDBACK-RENDERER: Content validation issues detected:', validation.issues);
  }
  
  return (
    <div className="relative">
      <div className="feedback-content">
        <ReactMarkdown
          components={{
            h1: ({ ...props }) => (
              <h1 {...props} className="text-2xl font-bold mb-4 mt-6 first:mt-0 text-foreground border-b border-border pb-2" />
            ),
            h2: ({ ...props }) => (
              <h2 {...props} className="text-xl font-semibold mb-3 mt-5 first:mt-0 text-foreground border-b border-border pb-1" />
            ),
            h3: ({ ...props }) => (
              <h3 {...props} className="text-lg font-medium mb-2 mt-4 first:mt-0 text-foreground" />
            ),
            h4: ({ ...props }) => (
              <h4 {...props} className="text-base font-medium mb-2 mt-3 first:mt-0 text-foreground" />
            ),
            p: ({ ...props }) => (
              <p {...props} className="mb-4 last:mb-0 leading-relaxed text-foreground" />
            ),
            ul: ({ ...props }) => (
              <ul {...props} className="list-disc pl-6 mb-4 space-y-2 text-foreground" />
            ),
            ol: ({ ...props }) => (
              <ol {...props} className="list-decimal pl-6 mb-4 space-y-2 text-foreground" />
            ),
            li: ({ ...props }) => (
              <li {...props} className="mb-1 leading-relaxed text-foreground" />
            ),
            strong: ({ ...props }) => (
              <strong {...props} className="font-semibold text-foreground" />
            ),
            em: ({ ...props }) => (
              <em {...props} className="italic text-muted-foreground" />
            ),
            blockquote: ({ ...props }) => (
              <blockquote {...props} className="border-l-4 border-primary pl-4 my-4 italic bg-muted py-3 rounded-r" />
            ),
            hr: ({ ...props }) => (
              <hr {...props} className="my-6 border-border" />
            ),
            code: ({ ...props }) => (
              <code {...props} className="bg-muted px-2 py-1 rounded text-sm font-mono text-foreground" />
            ),
            pre: ({ ...props }) => (
              <pre {...props} className="bg-muted p-4 rounded-lg mb-4 overflow-x-auto border border-border" />
            ),
            a: ({ ...props }) => (
              <a 
                {...props} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:opacity-80 underline hover:no-underline transition-all"
              />
            ),
          }}
        >
          {processedContent}
        </ReactMarkdown>
      </div>
      <FeedbackActionButtons content={content} />
    </div>
  );
};

// New JSON feedback renderer component
interface JSONFeedbackRendererProps {
  feedback: any;
}

const JSONFeedbackRenderer: React.FC<JSONFeedbackRendererProps> = ({ feedback }) => {
  // Check if this is a prediction feedback analysis
  if (feedback.type === 'prediction_feedback_analysis') {
    return <PredictionFeedbackRenderer feedback={feedback} />;
  }
  
  // Regular feedback analysis
  const { target_overview, user_impressions, analysis, overall_assessment } = feedback;
  
  return (
    <div className="feedback-content space-y-6">
      {/* Target Overview */}
      <div className="border-b border-border pb-4">
        <h2 className="text-xl font-semibold mb-3 text-foreground flex items-center gap-2">
          🎯 Target Overview
        </h2>
        <div className="text-foreground leading-relaxed space-y-2">
          <p><strong>Type:</strong> {target_overview.type}</p>
          <p><strong>Target:</strong> {target_overview.description}</p>
          {target_overview.surrounding_area && (
            <p><strong>Surrounding Area:</strong> {target_overview.surrounding_area}</p>
          )}
        </div>
      </div>
      
      {/* User's Impressions */}
      <div className="border-b border-border pb-4">
        <h2 className="text-xl font-semibold mb-3 text-foreground flex items-center gap-2">
          📝 Your Impressions
        </h2>
        <div className="space-y-3">
          {user_impressions.written_impressions && (
            <div>
              <h4 className="font-medium text-foreground mb-2">Written Impressions</h4>
              <p className="text-foreground leading-relaxed">{user_impressions.written_impressions}</p>
            </div>
          )}
          {user_impressions.user_images && (
            <div>
              <h4 className="font-medium text-foreground mb-2">User's Image(s)</h4>
              <p className="text-foreground leading-relaxed">{user_impressions.user_images}</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Analysis */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold mb-3 text-foreground flex items-center gap-2">
          📊 Analysis
        </h2>
        
        {/* Matches */}
        {analysis.matches?.length > 0 && (
          <div>
            <h3 className="text-lg font-medium mb-3 text-foreground flex items-center gap-2">
              ✅ Matches
            </h3>
            <ul className="list-disc pl-6 space-y-2">
              {analysis.matches.map((item: string, idx: number) => (
                <li key={idx} className="text-foreground leading-relaxed">{item.replace(/^✅\s*/, '')}</li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Mismatches */}
        {analysis.mismatches?.length > 0 && (
          <div>
            <h3 className="text-lg font-medium mb-3 text-foreground flex items-center gap-2">
              ❌ Mismatches
            </h3>
            <ul className="list-disc pl-6 space-y-2">
              {analysis.mismatches.map((item: string, idx: number) => (
                <li key={idx} className="text-foreground leading-relaxed">{item.replace(/^❌\s*/, '')}</li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Partial Connections */}
        {analysis.partial_connections?.length > 0 && (
          <div>
            <h3 className="text-lg font-medium mb-3 text-foreground flex items-center gap-2">
              🔄 Partial Connections
            </h3>
            <ul className="list-disc pl-6 space-y-2">
              {analysis.partial_connections.map((item: string, idx: number) => (
                <li key={idx} className="text-foreground leading-relaxed">{item.replace(/^🔄\s*/, '')}</li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Additional Observations */}
        {analysis.additional_observations?.length > 0 && (
          <div>
            <h3 className="text-lg font-medium mb-3 text-foreground flex items-center gap-2">
              💡 Additional Observations
            </h3>
            <ul className="list-disc pl-6 space-y-2">
              {analysis.additional_observations.map((item: string, idx: number) => (
                <li key={idx} className="text-foreground leading-relaxed">{item.replace(/^💡\s*/, '')}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
      
      {/* Overall Assessment */}
      <div className="border-t border-border pt-4">
        <h2 className="text-xl font-semibold mb-3 text-foreground flex items-center gap-2">
          ⚖️ Overall Assessment
        </h2>
        <p className="text-foreground leading-relaxed">{overall_assessment}</p>
      </div>
    </div>
  );
};

// Prediction feedback renderer component
const PredictionFeedbackRenderer: React.FC<JSONFeedbackRendererProps> = ({ feedback }) => {
  const { target_overview, user_impressions, analysis, overall_assessment } = feedback;
  
  return (
    <div className="feedback-content space-y-6">
      {/* Target Overview */}
      <div className="border-b border-border pb-4">
        <h2 className="text-xl font-semibold mb-3 text-foreground flex items-center gap-2">
          🔮 Prediction Target Analysis
        </h2>
        <div className="text-foreground leading-relaxed space-y-3">
          <div className="bg-muted/50 p-3 rounded-lg">
            <p className="font-medium text-sm text-muted-foreground mb-1">Option A (YES)</p>
            <p>{target_overview.optionA_description}</p>
          </div>
          <div className="bg-muted/50 p-3 rounded-lg">
            <p className="font-medium text-sm text-muted-foreground mb-1">Option B (NO)</p>
            <p>{target_overview.optionB_description}</p>
          </div>
        </div>
      </div>
      
      {/* User's Impressions */}
      <div className="border-b border-border pb-4">
        <h2 className="text-xl font-semibold mb-3 text-foreground flex items-center gap-2">
          📝 Your Impressions
        </h2>
        <div className="space-y-3">
          {user_impressions?.written_impressions && (
            <div>
              <h4 className="font-medium text-foreground mb-2">Written Impressions</h4>
              <p className="text-foreground leading-relaxed">{user_impressions.written_impressions}</p>
            </div>
          )}
          {user_impressions?.user_images && (
            <div>
              <h4 className="font-medium text-foreground mb-2">Your Image(s)</h4>
              <p className="text-foreground leading-relaxed">{user_impressions.user_images}</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Analysis - Option A */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold mb-3 text-foreground flex items-center gap-2">
          📊 Comparison Analysis
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Option A Analysis */}
          <div className="bg-muted/30 p-4 rounded-lg">
            <h3 className="text-lg font-medium mb-3 text-foreground">Option A (YES)</h3>
            
            {analysis.optionA_matches?.length > 0 && (
              <div className="mb-3">
                <p className="text-sm font-medium text-green-500 mb-2">✅ Matches</p>
                <ul className="list-disc pl-5 space-y-1 text-sm">
                  {analysis.optionA_matches.map((item: string, idx: number) => (
                    <li key={idx} className="text-foreground">{item.replace(/^✅\s*/, '')}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {analysis.optionA_mismatches?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-red-500 mb-2">❌ Mismatches</p>
                <ul className="list-disc pl-5 space-y-1 text-sm">
                  {analysis.optionA_mismatches.map((item: string, idx: number) => (
                    <li key={idx} className="text-foreground">{item.replace(/^❌\s*/, '')}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          
          {/* Option B Analysis */}
          <div className="bg-muted/30 p-4 rounded-lg">
            <h3 className="text-lg font-medium mb-3 text-foreground">Option B (NO)</h3>
            
            {analysis.optionB_matches?.length > 0 && (
              <div className="mb-3">
                <p className="text-sm font-medium text-green-500 mb-2">✅ Matches</p>
                <ul className="list-disc pl-5 space-y-1 text-sm">
                  {analysis.optionB_matches.map((item: string, idx: number) => (
                    <li key={idx} className="text-foreground">{item.replace(/^✅\s*/, '')}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {analysis.optionB_mismatches?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-red-500 mb-2">❌ Mismatches</p>
                <ul className="list-disc pl-5 space-y-1 text-sm">
                  {analysis.optionB_mismatches.map((item: string, idx: number) => (
                    <li key={idx} className="text-foreground">{item.replace(/^❌\s*/, '')}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
        
        {/* Alignment Result */}
        {analysis.stronger_alignment && (
          <div className="bg-primary/10 p-4 rounded-lg border border-primary/20">
            <p className="font-medium text-foreground mb-2">
              🎯 Stronger Alignment: {analysis.stronger_alignment === 'neither' ? 'Neither option' : `Option ${analysis.stronger_alignment}`}
            </p>
            {analysis.alignment_reasoning && (
              <p className="text-sm text-muted-foreground">{analysis.alignment_reasoning}</p>
            )}
          </div>
        )}
      </div>
      
      {/* Overall Assessment */}
      <div className="border-t border-border pt-4">
        <h2 className="text-xl font-semibold mb-3 text-foreground flex items-center gap-2">
          ⚖️ Overall Assessment
        </h2>
        <p className="text-foreground leading-relaxed">{overall_assessment}</p>
      </div>
    </div>
  );
};

export default FeedbackRenderer;
